import deepmerge from "deepmerge";
import isOneWebDomainRuntime from "./shared/avaAppRuntime";
import { Environments } from "./shared/constants";
import { getRuntimeConfig } from "./utils/runtimeConfig";

/**
 * FVS Configuration IDs by environment
 * These are kept separate from runtime config to avoid exposing them in the browser console
 */
const FVS_CONFIGURATION_IDS: Record<string, string> = {
    local: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
    dev: "16e6001b-95d2-42de-8b3a-185a15b444af",
    qa: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
    sbx: "fc747193-a22a-405e-bc06-0c582fde6a98",
    prd: "13dc2391-5825-46a3-a31e-afb7d50f11d2",
};

/**
 * Get FVS Configuration ID based on environment
 * @param env - The environment name (local, dev, qa, sbx, prd)
 * @returns The FVS configuration ID for the given environment
 */
export function getFvsConfigurationId(env: string): string {
    const normalizedEnv = env?.toLowerCase() || "qa";
    return FVS_CONFIGURATION_IDS[normalizedEnv] || FVS_CONFIGURATION_IDS.qa;
}

// Helper function to safely get environment variables
// Prioritizes runtime config over build-time environment variables
const getEnvVar = (key: string): string => {
    // Check if runtime config is available first
    const runtimeConfig = getRuntimeConfig();

    if (runtimeConfig && key in runtimeConfig) {
        return runtimeConfig[key as keyof typeof runtimeConfig] || "";
    }

    // Fallback to build-time environment variables (for local development)
    return (import.meta.env[key] as string) || "";
};

interface HostnamesConfig {
    identity: string;
    avatax: string;
    cupHost: string;
    ecmApiHost: string;
    docLockerClient: string;
    certCapture: string;
    genCert: string;
    genCertAPI: string;
    certExpress: string;
    dataService: string;
    avaApp?: string;
}

interface EnvironmentConfig {
    cupHost: string;
    ecmApiHost: string;
    docLockerClient: string;
    certCapture: string;
    genCert: string;
    genCertAPI: string;
    certExpress: string;
    fvsConfigurationId: string;
    apiVersion: string;
    apiVersion3: string;
    dataService: string;
    enableGlobalImports: boolean;
}

export interface AppConfig {
    appEnv?: string;
    hostnames: HostnamesConfig;
    fvsConfigurationId?: string;
    apiVersion?: string;
    apiVersion3?: string;
    assetsPath?: string;
    enableGlobalImports?: boolean;
}

const ecmConfigForAvaApp: Record<Environments, EnvironmentConfig> = {
    [Environments.LOCAL]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web-exemptions.edge.qa.us-east-1.aws.avalara.io",
        docLockerClient: "avaapp.gamma.qa.us-west-2.aws.avalara.io/document-locker",
        certCapture: "https://staging.certcapture.com",
        genCert: "http://127.0.0.1:9001",
        genCertAPI: "http:localhost:7153",
        certExpress: "https://staging.certexpress.com",
        fvsConfigurationId: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
        enableGlobalImports: true,
    },
    [Environments.DEV]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web-exemptions.gamma.dev.us-west-2.aws.avalara.io",
        docLockerClient: "avaapp.gamma.qa.us-west-2.aws.avalara.io/document-locker",
        certCapture: "https://staging.certcapture.com",
        genCert: "https://dev.gencert.exemptions.avalara.io",
        genCertAPI: "https://api.exemptions.dev.avalara.io",
        certExpress: "https://test-cc7-ce.dev.certcapture.com",
        fvsConfigurationId: "16e6001b-95d2-42de-8b3a-185a15b444af",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
        enableGlobalImports: true,
    },
    [Environments.QA]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web-exemptions.edge.qa.us-east-1.aws.avalara.io",
        docLockerClient: "avaapp.gamma.qa.us-west-2.aws.avalara.io/document-locker",
        certCapture: "https://staging.certcapture.com",
        genCert: "https://qa.gencert.exemptions.avalara.io",
        genCertAPI: "https://api.exemptions.qa.avalara.io",
        certExpress: "https://staging.certexpress.com",
        fvsConfigurationId: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
        enableGlobalImports: true,
    },
    [Environments.SBX]: {
        cupHost: "sandbox.admin.avalara.com",
        ecmApiHost: "api.sbx.web.exemptions.avalara.com",
        docLockerClient: "app.sbx.avalara.com/document-locker",
        certCapture: "https://sbx.certcapture.com",
        genCert: "https://sbx.gencert.exemptions.avalara.com",
        genCertAPI: "https://api.sbx.exemptions.avalara.com",
        certExpress: "https://sbx.certexpress.com",
        fvsConfigurationId: "fc747193-a22a-405e-bc06-0c582fde6a98",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://sandbox.data.webplatform.avalara.com",
        enableGlobalImports: true,
    },
    [Environments.PRD]: {
        cupHost: "admin.avalara.com",
        ecmApiHost: "api.web.exemptions.avalara.com",
        docLockerClient: "app.avalara.com/document-locker",
        certCapture: "https://app.certcapture.com",
        genCert: "https://gencert.exemptions.avalara.com",
        genCertAPI: "https://api.exemptions.avalara.com",
        certExpress: "https://app.certexpress.com",
        fvsConfigurationId: "13dc2391-5825-46a3-a31e-afb7d50f11d2",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://data.webplatform.avalara.com",
        enableGlobalImports: true,
    },
};

export default function getConfig(): AppConfig {
    // when running in AvaApp do not use configs from environment variables (.env files)
    if (isOneWebDomainRuntime()) {
        const avaAppConfig = window.AvaAppConfig;
        const appEnv = avaAppConfig?.env as Environments;
        const envConfig = appEnv ? ecmConfigForAvaApp[appEnv] : undefined;

        // Global Imports is env-driven (like standalone config.{env}.json). The shell may
        // override per deployment via AvaAppConfig.VITE_ENABLE_GLOBAL_IMPORTS ("true"/"false").
        const avaAppGlobalImports = avaAppConfig?.VITE_ENABLE_GLOBAL_IMPORTS;
        const enableGlobalImports =
            typeof avaAppGlobalImports === "string"
                ? avaAppGlobalImports === "true"
                : (envConfig?.enableGlobalImports ?? false);

        const ecmConfig: AppConfig = {
            appEnv,
            enableGlobalImports,
            hostnames: {
                identity: avaAppConfig?.hostnames?.identity || "",
                avatax: avaAppConfig?.hostnames?.avatax || "",
                cupHost: envConfig?.cupHost || "",
                ecmApiHost: envConfig?.ecmApiHost || "",
                docLockerClient: envConfig?.docLockerClient || "",
                certCapture: envConfig?.certCapture || "",
                genCert: envConfig?.genCert || "",
                genCertAPI: envConfig?.genCertAPI || "",
                certExpress: envConfig?.certExpress || "",
                dataService: envConfig?.dataService || "",
                avaApp: avaAppConfig?.hostnames?.avaApp || "",
            },
            fvsConfigurationId: envConfig?.fvsConfigurationId,
            apiVersion: envConfig?.apiVersion,
            apiVersion3: envConfig?.apiVersion3,
        };

        return deepmerge(ecmConfig, avaAppConfig || {}) as AppConfig;
    }
    return {
        appEnv: getEnvVar("VITE_ENV"),
        assetsPath: "",
        enableGlobalImports: getEnvVar("VITE_ENABLE_GLOBAL_IMPORTS") === "true",
        hostnames: {
            identity: getEnvVar("VITE_IDENTITY_HOST"),
            avatax: getEnvVar("VITE_AVATAX_API_HOST"),
            cupHost: getEnvVar("VITE_CUP_HOST"),
            ecmApiHost: getEnvVar("VITE_API_HOST_BASE"),
            docLockerClient: getEnvVar("VITE_DOCLOCKER_HOST"),
            certCapture: getEnvVar("VITE_CERTCAPTURE"),
            genCert: getEnvVar("VITE_GENCERT_URL"),
            genCertAPI: getEnvVar("VITE_GENCERT_API_URL"),
            certExpress: getEnvVar("VITE_CERTEXPRESS_URL"),
            dataService: getEnvVar("VITE_DATASERVICE_URL"),
            avaApp: getEnvVar("VITE_AVA_APP_HOST"),
        },
        fvsConfigurationId: getFvsConfigurationId(getEnvVar("VITE_ENV")),
        apiVersion: getEnvVar("VITE_API_VERSION"),
        apiVersion3: getEnvVar("VITE_API_VERSION3"),
    };
}
